import { v4 as uuid } from 'uuid';
import CoreBase from '../../../services/engines/CoreBase';
import userSettingsEngine from '../../../controllers/UserSettings/UserSettingsEngine';
import { OUTPUT_MSG_TYPES, DEFAULT_OUTPUT_MSG_TYPE } from '../Types/OutputMsgTypes';

const DEFAULT_CACHE_SIZE = 300;

/**
 * OutputMsgEngine — generic message log, shared across features (UmlWorkspace,
 * CsCodeGen, etc). Each caller passes its own `cat` so messages from different
 * features can coexist in one window. Newest message goes to index 0.
 * Singleton, pure in-memory — no fetch/save, no backend.
 */
class OutputMsgEngine extends CoreBase {
    _name = 'OutputMsgEngine';
    _messages = [];
    _listeners = [];

    /**
     * Called once from PageContent — no async work, just logging setup.
     */
    bootup = () => {
        this.logDebug('bootup', 'Enter');
    };

    subscribe = (callback) => {
        this._listeners.push(callback);
        return () => {
            this._listeners = this._listeners.filter((listener) => listener !== callback);
        };
    };

    _notify = () => {
        this._listeners.forEach((listener) => listener());
    };

    /**
     * Adds a message, newest first. Unknown type falls back to 'log'.
     * @param {string} cat - feature/category, e.g. 'UmlWorkspace', 'CsCodeGen'
     * @param {string} type - one of OUTPUT_MSG_TYPES
     * @param {string} text
     */
    addMsg = (cat, type, text) => {
        try {
            const safeType = OUTPUT_MSG_TYPES.includes(type) ? type : DEFAULT_OUTPUT_MSG_TYPE;
            const cacheSize = userSettingsEngine.resolve('msgWindow.cacheSize') || DEFAULT_CACHE_SIZE;
            this._messages.unshift({ id: uuid(), cat, type: safeType, text, timestamp: Date.now() });
            if (this._messages.length > cacheSize) {
                this._messages.length = cacheSize;
            }
            this._notify();
        } catch (error) {
            this.logError('addMsg', error.message);
        }
    };

    /**
     * Returns all cached messages, newest first.
     * @returns {object[]}
     */
    getMessages = () => this._messages;

    clear = () => {
        this._messages = [];
        this._notify();
    };
}

const outputMsgEngine = new OutputMsgEngine();
export default outputMsgEngine;
